import { Button, Flex, Text } from '@thenexlabs/uikit' // Added Text
import { useWeb3React } from '@web3-react/core'
import { BigNumber } from 'ethers'
import { formatUnits } from 'ethers/lib/utils' // Added for formatting
import { useTranslation } from 'contexts/Localization'
import { ToastDescriptionWithTx } from 'components/Toast'
import useToast from 'hooks/useToast'
import React, { useMemo, useState } from 'react'
import { useAppDispatch } from 'state'
import { logError } from 'utils/sentry'
import { fetchPublicDataAsyncGLITCH } from 'state/glitch'
import useMintGLITCH from 'hooks/glitch/useMintGLITCH'
import { useGetXpBalance } from 'hooks/useTokenBalance'

interface GlitchActionsProps {
  price?: string
  mintAmount?: string
}

const MintAction: React.FC<GlitchActionsProps> = ({ price, mintAmount }) => {
  const { account } = useWeb3React()
  const { t } = useTranslation()
  const { toastSuccess, toastError } = useToast()
  const dispatch = useAppDispatch()
  const [pendingTx, setPendingTx] = useState(false)

  const { onMint } = useMintGLITCH(price, mintAmount, account)
  // --- FIX 1: Removed fetchStatus as it's unreliable ---
  const { balance: xpBalance } = useGetXpBalance()

  // Format the raw BigNumber balance into a readable string
  const formattedBalance = useMemo(() => {
    // Assuming XP has 18 decimals, adjust if different
    const balanceAsString = formatUnits(xpBalance, 18)
    return parseFloat(balanceAsString).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    })
  }, [xpBalance])

  const priceAsBn = useMemo(() => {
    try {
      return BigNumber.from(price || '0')
    } catch (error) {
      console.error('Invalid price format for BigNumber:', price)
      return BigNumber.from('0')
    }
  }, [price])

  const hasSufficientBalance = useMemo(() => xpBalance.gte(priceAsBn), [xpBalance, priceAsBn])

  // --- FIX 2: Removed isFetchingBalance from the disabled logic ---
  const isButtonDisabled = !account || pendingTx || !hasSufficientBalance || priceAsBn.isZero()

  // --- FIX 3: Removed the "Checking Balance..." state from the button text ---
  const getButtonText = () => {
    if (pendingTx) {
      return t('Minting...')
    }
    if (!account) {
      return t('Connect Wallet')
    }
    if (!hasSufficientBalance) {
      return t('Not enough XP')
    }
    return t('Mint')
  }

  const handleMint = async () => {
    setPendingTx(true)
    try {
      await onMint(
        (tx) => {
          toastSuccess(`${t('Transaction Submitted')}!`, <ToastDescriptionWithTx txHash={tx.hash} />)
        },
        (receipt) => {
          toastSuccess(
            `${t('Unlocked')}!`,
            <ToastDescriptionWithTx txHash={receipt.transactionHash}>
              {t('Your item has been minted!')}
            </ToastDescriptionWithTx>,
          )
        },
        (receipt) => {
          toastError(
            t('Error'),
            <ToastDescriptionWithTx txHash={receipt.transactionHash}>
              {t('Please try again. Confirm the transaction and make sure you are paying enough gas!')}
            </ToastDescriptionWithTx>,
          )
        },
      )
    } catch (e) {
      toastError(t('Error'), t('Please try again. Confirm the transaction and make sure you are paying enough gas!'))
      logError(e)
    } finally {
      setPendingTx(false)
      dispatch(fetchPublicDataAsyncGLITCH(account))
    }
  }

  return (
    // Changed to a column layout to stack the balance and button
    <Flex flexDirection="column" alignItems="center" width="100%">
      {/* Balance Display */}
      <Flex justifyContent="space-between" width="100%" mb="16px">
        <Text color="textSubtle">{t('Your XP Balance')}:</Text>
        <Text bold>{formattedBalance}</Text>
      </Flex>

      {/* Mint Button */}
      <Button width="100%" variant="primary" onClick={handleMint} disabled={isButtonDisabled}>
        {getButtonText()}
      </Button>
    </Flex>
  )
}

export default MintAction